import React, { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import HighQuality from '@mui/icons-material/HighQuality';
import Settings from '@mui/icons-material/Settings';
import Lock from '@mui/icons-material/Lock';
import CheckCircle from '@mui/icons-material/CheckCircle';
import SignalCellular4Bar from '@mui/icons-material/SignalCellular4Bar';
import SignalCellular3Bar from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular2Bar from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular1Bar from '@mui/icons-material/SignalCellular1Bar';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../lib/PlayerContext';
import { AudioQuality, SubscriptionTier } from '../types/models';
import { AudioQualitySettings } from './AudioQualitySettings';
import {
  AUDIO_QUALITY_INFO,
  QUALITY_LABELS,
  canAccessQuality,
} from '../constants/audioQuality';

interface AudioQualityIndicatorProps {
  variant?: 'chip' | 'icon' | 'compact';
  showSettings?: boolean;
}

export function AudioQualityIndicator({ 
  variant = 'chip', 
  showSettings = true 
}: AudioQualityIndicatorProps) {
  const { user } = useAuth();
  const { audioQuality, setAudioQuality, currentSong, getAvailableQualities } = usePlayer();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const userTier: SubscriptionTier = user?.subscriptionTier || 'free';
  const availableQualities = currentSong ? getAvailableQualities(currentSong) : [];
  const currentInfo = AUDIO_QUALITY_INFO[audioQuality];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleQualitySelect = (quality: AudioQuality) => {
    if (canAccessQuality(userTier, quality) && availableQualities.includes(quality)) {
      setAudioQuality(quality);
    }
    handleMenuClose();
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
    handleMenuClose();
  };

  const getQualityIcon = (quality: AudioQuality) => {
    switch (quality) {
      case 'max':
        return <SignalCellular4Bar />;
      case 'high':
        return <SignalCellular3Bar />;
      case 'medium':
        return <SignalCellular2Bar />;
      case 'low':
      default:
        return <SignalCellular1Bar />;
    }
  };



  if (variant === 'icon') {
    return (
      <Box>
        <Tooltip title={`Audio Quality: ${QUALITY_LABELS[audioQuality]}`}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              color: '#B8B8C0',
              backgroundColor: 'transparent',
              border: '1px solid #3A3A42',
              borderRadius: '6px',
              padding: '6px',
              '&:hover': {
                backgroundColor: 'rgba(255, 77, 140, 0.1)',
                borderColor: '#FF4D8C',
                color: '#F8F9FA',
              },
              '&:focus': {
                outline: '2px solid #FF4D8C',
                outlineOffset: '2px',
              }
            }}
          >
            {getQualityIcon(audioQuality)}
          </IconButton>
        </Tooltip>
        
        <QualityMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          currentQuality={audioQuality}
          availableQualities={availableQualities}
          userTier={userTier}
          onQualitySelect={handleQualitySelect}
          onSettingsOpen={showSettings ? handleSettingsOpen : undefined}
        />
        
        {showSettings && (
          <AudioQualitySettings
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
        )}
      </Box>
    );
  }

  if (variant === 'compact') {
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          {currentInfo.bitrate}
        </Typography>
        {getQualityIcon(audioQuality)}
      </Box>
    );
  }

  // Default chip variant
  return (
    <Box>
      <Chip
        icon={<HighQuality sx={{ color: '#B8B8C0' }} />}
        label={QUALITY_LABELS[audioQuality]}
        size="small"
        onClick={handleMenuOpen}
        clickable
        variant="outlined"
        sx={{
          backgroundColor: 'transparent',
          border: '1px solid #3A3A42',
          color: '#F8F9FA',
          fontFamily: 'Inter',
          fontSize: '12px',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: 'rgba(255, 77, 140, 0.1)',
            borderColor: '#FF4D8C',
          },
          '&:focus': {
            outline: '2px solid #FF4D8C',
            outlineOffset: '2px',
          },
          '& .MuiChip-label': {
            color: '#F8F9FA',
            fontFamily: 'Inter',
            fontSize: '12px',
          },
          '& .MuiChip-icon': {
            color: '#B8B8C0',
          }
        }}
      />
      
      <QualityMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        currentQuality={audioQuality}
        availableQualities={availableQualities}
        userTier={userTier}
        onQualitySelect={handleQualitySelect}
        onSettingsOpen={showSettings ? handleSettingsOpen : undefined}
      />
      
      {showSettings && (
        <AudioQualitySettings
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </Box>
  );
}

interface QualityMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  currentQuality: AudioQuality;
  availableQualities: readonly AudioQuality[];
  userTier: SubscriptionTier;
  onQualitySelect: (quality: AudioQuality) => void;
  onSettingsOpen?: () => void;
}

function QualityMenu({
  anchorEl,
  open,
  onClose,
  currentQuality,
  availableQualities,
  userTier,
  onQualitySelect,
  onSettingsOpen,
}: QualityMenuProps) {
  const qualities: AudioQuality[] = ['max', 'high', 'medium', 'low'];

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { 
          minWidth: 220,
          backgroundColor: '#1A1A1F',
          border: '1px solid #3A3A42',
          borderRadius: '8px',
          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23), 0 0 40px rgba(255, 77, 140, 0.1)',
        }
      }}
      sx={{
        '& .MuiMenuItem-root': {
          color: '#F8F9FA',
          fontFamily: 'Inter',
          fontSize: '14px',
          '&:hover': {
            backgroundColor: 'rgba(255, 77, 140, 0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 77, 140, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255, 77, 140, 0.2)',
            }
          },
          '&.Mui-disabled': {
            color: '#7A7A85',
            opacity: 0.6,
          }
        }
      }}
    >
      <MenuItem disabled sx={{ 
        backgroundColor: '#242429',
        borderBottom: '1px solid #3A3A42',
        marginBottom: '4px',
        opacity: '1 !important',
      }}>
        <ListItemText
          primary={
            <Typography sx={{ color: '#F8F9FA', fontFamily: 'Inter', fontSize: '14px', fontWeight: 600 }}>
              Audio Quality
            </Typography>
          }
          secondary={
            <Typography sx={{ color: '#B8B8C0', fontFamily: 'Inter', fontSize: '12px' }}>
              Current: {QUALITY_LABELS[currentQuality]}
            </Typography>
          }
        />
      </MenuItem>
      
      {qualities.map((quality) => {
        const info = AUDIO_QUALITY_INFO[quality];
        const isAccessible = canAccessQuality(userTier, quality);
        const isAvailable = availableQualities.includes(quality);
        const isCurrent = quality === currentQuality;
        const isDisabled = !isAccessible || !isAvailable;

        return (
          <MenuItem
            key={quality}
            onClick={() => onQualitySelect(quality)}
            disabled={isDisabled}
            selected={isCurrent}
            sx={{ margin: '2px 4px', borderRadius: '6px' }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              {isCurrent ? (
                <CheckCircle sx={{ color: '#FF4D8C' }} />
              ) : !isAccessible ? (
                <Lock sx={{ color: '#7A7A85' }} />
              ) : (
                <HighQuality sx={{ color: isAvailable ? '#B8B8C0' : '#7A7A85' }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography sx={{ color: isDisabled ? '#7A7A85' : '#F8F9FA', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }}>
                  {QUALITY_LABELS[quality]}
                </Typography>
              }
              secondary={
                <Typography sx={{ color: '#B8B8C0', fontFamily: 'Inter', fontSize: '12px' }}>
                  {`${info.bitrate} • ${info.format}`}
                </Typography>
              }
            />
          </MenuItem>
        );
      })}
      
      {onSettingsOpen && (
        <>
          <Box sx={{ borderTop: '1px solid #3A3A42', margin: '4px 0' }} />
          <MenuItem 
            onClick={onSettingsOpen}
            sx={{ margin: '2px 4px', borderRadius: '6px' }}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <Settings sx={{ color: '#B8B8C0' }} />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography sx={{ color: '#F8F9FA', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500 }}>
                  Quality Settings
                </Typography>
              }
            />
          </MenuItem>
        </>
      )}
    </Menu>
  );
}